import type { ReactNode } from 'react';
import { EmptyValue } from '@/components/common/EmptyValue';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { ROW_HOVER_BG, TABLE_HEADER_CLASS } from '@/styles/dataTableBg';
import { formatNumber } from '@/utils/number';

export interface DetailLineColumn<TLine> {
  header: string;
  isNumeric?: boolean;
  render: (line: TLine) => ReactNode;
}

interface DetailLinesTableProps<TLine> {
  title: string;
  lines: TLine[];
  columns: DetailLineColumn<TLine>[];
  getLineKey: (line: TLine) => number;
}

export function DetailLinesTable<TLine>({
  title,
  lines,
  columns,
  getLineKey,
}: DetailLinesTableProps<TLine>) {
  return (
    <section className="flex flex-col gap-2.5" aria-label={title}>
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        {title}
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-2xs font-semibold tabular-nums',
            ACCENT_MARK_CLASS,
          )}
        >
          {formatNumber(lines.length)}
        </span>
      </h3>
      <div className="overflow-x-auto rounded-xl bg-card ring-1 ring-primary/10">
        <table className="w-full text-sm">
          <thead className={cn(TABLE_HEADER_CLASS, 'text-xs-plus')}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.header}
                  className={cn(
                    'px-3 py-2.5 font-semibold',
                    column.isNumeric ? 'text-right' : 'text-left',
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {lines.map((line) => (
              <tr key={getLineKey(line)} className={cn('transition-colors', ROW_HOVER_BG)}>
                {columns.map((column) => (
                  <td
                    key={column.header}
                    className={cn('px-3 py-2.5', column.isNumeric && 'text-right tabular-nums')}
                  >
                    {column.render(line) ?? <EmptyValue />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
